import { useAuth } from "../contexts/AuthContext";
import { useMemo, useCallback } from "react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

/**
 * Lê o corpo da resposta tratando os formatos da API (docs/api.md 1.5):
 * envelope { data: [...] }, JSON direto, primitivo (número/boolean) e 204 sem corpo.
 */
const parseBody = async (response) => {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    return text;
  }
};

/**
 * Monta o Error a partir dos dois formatos de erro da API (docs/api.md 1.6):
 * plano { code, message } ou envelope { error: { code, description } }.
 * O erro sai com .status, .code e .data (corpo original).
 */
const buildApiError = async (response, method, endpoint) => {
  let errorData;
  try {
    errorData = await parseBody(response);
  } catch (e) {
    errorData = null;
  }
  if (!errorData || typeof errorData !== "object") {
    errorData = { message: `Erro ${response.status}: ${response.statusText}` };
  }

  const message =
    errorData.message ??
    errorData.error?.description ??
    `Erro na requisição ${method} ${endpoint}: ${response.status}`;
  const code = errorData.code ?? errorData.error?.code;

  const error = new Error(message);
  error.status = response.status;
  error.code = code;
  error.data = { ...errorData, message, code };
  return error;
};

/**
 * Hook customizado para fazer chamadas autenticadas para a API
 * Automaticamente inclui o token JWT nas requisições
 */
export const useApi = () => {
  const { apiCall, getAuthHeaders } = useAuth();

  // Função genérica para fazer requisições GET
  const get = useCallback(
    async (endpoint, options = {}) => {
      try {
        const response = await apiCall(`${BACKEND_URL}${endpoint}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...options.headers,
          },
          ...options,
        });

        if (!response.ok) {
          throw await buildApiError(response, "GET", endpoint);
        }

        return await parseBody(response);
      } catch (error) {
        console.error(`Erro na requisição GET ${endpoint}:`, error);
        throw error;
      }
    },
    [apiCall]
  );

  // Função genérica para fazer requisições POST
  const post = useCallback(
    async (endpoint, data = null, options = {}) => {
      try {
        const response = await apiCall(`${BACKEND_URL}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...options.headers,
          },
          body: data ? JSON.stringify(data) : null,
          ...options,
        });

        if (!response.ok) {
          throw await buildApiError(response, "POST", endpoint);
        }

        return await parseBody(response);
      } catch (error) {
        console.error(`Erro na requisição POST ${endpoint}:`, error);
        throw error;
      }
    },
    [apiCall]
  );

  // Função genérica para fazer requisições PUT
  const put = useCallback(
    async (endpoint, data = null, options = {}) => {
      try {
        const response = await apiCall(`${BACKEND_URL}${endpoint}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...options.headers,
          },
          body: data ? JSON.stringify(data) : null,
          ...options,
        });

        if (!response.ok) {
          throw await buildApiError(response, "PUT", endpoint);
        }

        return await parseBody(response);
      } catch (error) {
        console.error(`Erro na requisição PUT ${endpoint}:`, error);
        throw error;
      }
    },
    [apiCall]
  );

  // Função genérica para fazer requisições DELETE
  const del = useCallback(
    async (endpoint, options = {}) => {
      try {
        const response = await apiCall(`${BACKEND_URL}${endpoint}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...options.headers,
          },
          ...options,
        });

        if (!response.ok) {
          throw await buildApiError(response, "DELETE", endpoint);
        }

        return await parseBody(response);
      } catch (error) {
        console.error(`Erro na requisição DELETE ${endpoint}:`, error);
        throw error;
      }
    },
    [apiCall]
  );

  // Função genérica para fazer requisições PATCH
  const patch = useCallback(
    async (endpoint, data = null, options = {}) => {
      try {
        const response = await apiCall(`${BACKEND_URL}${endpoint}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...options.headers,
          },
          body: data ? JSON.stringify(data) : null,
          ...options,
        });

        if (!response.ok) {
          throw await buildApiError(response, "PATCH", endpoint);
        }

        return await parseBody(response);
      } catch (error) {
        console.error(`Erro na requisição PATCH ${endpoint}:`, error);
        throw error;
      }
    },
    [apiCall]
  );

  // Função genérica para fazer requisições OPTIONS
  const options = useCallback(
    async (endpoint, requestOptions = {}) => {
      try {
        const response = await apiCall(`${BACKEND_URL}${endpoint}`, {
          method: "OPTIONS",
          headers: {
            "Content-Type": "application/json",
            ...requestOptions.headers,
          },
          ...requestOptions,
        });

        if (!response.ok) {
          throw await buildApiError(response, "OPTIONS", endpoint);
        }

        return await parseBody(response);
      } catch (error) {
        console.error(`Erro na requisição OPTIONS ${endpoint}:`, error);
        throw error;
      }
    },
    [apiCall]
  );

  return useMemo(
    () => ({
      get,
      post,
      put,
      patch,
      delete: del,
      options,
      getAuthHeaders,
      apiCall,
    }),
    [get, post, put, patch, del, options, getAuthHeaders, apiCall]
  );
};
